/** True/false step — two large side-by-side buttons with keyboard shortcut support. */
import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Check, X } from 'lucide-react';
import { useSession } from '../SessionContext';
import { AnimatedKbd, useShortcutOverlay } from '../components/ShortcutOverlay';
import { useKeyboardRipple, RippleFlash } from '../components/KeyboardRipple';
import { StepShell } from './StepShell';
import {
  PromptRenderer,
  type TiptapNode,
  type MediaItem,
} from '@/shared/components/PromptRenderer';
import { normaliseMediaRefs } from '../utils/normaliseMediaRefs';
import { FeedbackDisplay } from './FeedbackDisplay';

interface DiagramJson {
  syntax: string;
  diagramType?: string;
}

/**
 * True/False step — simplified MCQ with two large side-by-side buttons.
 */
export function TrueFalseStep() {
  const { currentStep, state, dispatch } = useSession();
  const { showShortcuts } = useShortcutOverlay();
  const { rippleIndex, rippleKey } = useKeyboardRipple();

  const snapshot = currentStep?.stepSnapshotPublicJson;

  const answer = state.answer?.type === 'true_false' ? state.answer : null;
  const selected = answer?.selected ?? null;
  const phase = state.phase;
  const result = state.result;
  const isDisabled = phase === 'submitting' || phase === 'grading';
  const isGraded = phase === 'graded';

  // Correct answer comes from the grading result, NOT config (config strips it to prevent cheating)
  const correctAnswer = isGraded ? result?.correctChoiceIds?.[0] === 'true' : null;
  const [hoveredValue, setHoveredValue] = useState<boolean | null>(null);

  const mediaItems: MediaItem[] | null = normaliseMediaRefs(currentStep?.mediaRefsJson);

  const diagramJson = (currentStep as unknown as Record<string, unknown>)?.diagramJson as
    | DiagramJson
    | null
    | undefined;

  if (!currentStep || !snapshot) return null;

  function handleSelect(value: boolean) {
    if (isDisabled || isGraded) return;
    dispatch({ type: 'UPDATE_ANSWER', answer: { type: 'true_false', selected: value } });
  }

  function getButtonStyle(value: boolean): React.CSSProperties {
    const isSelected = selected === value;

    if (isGraded && correctAnswer !== null) {
      const isCorrectChoice = value === correctAnswer;
      if (isSelected && isCorrectChoice) {
        return {
          borderColor: 'var(--f-success)',
          backgroundColor: 'var(--f-success-subtle)',
          color: 'var(--f-success-text)',
        };
      }
      if (isSelected && !isCorrectChoice) {
        return {
          borderColor: 'var(--f-error)',
          backgroundColor: 'var(--f-error-subtle)',
          color: 'var(--f-error-text)',
        };
      }
      if (!isSelected && isCorrectChoice) {
        return {
          borderColor: 'var(--f-success)',
          backgroundColor: 'var(--f-success-subtle)',
          color: 'var(--f-success-text)',
        };
      }
      return {
        borderColor: 'var(--f-border)',
        backgroundColor: 'var(--f-bg-raised)',
        color: 'var(--f-fg-muted)',
      };
    }

    if (isSelected) {
      return {
        borderColor: 'var(--f-accent)',
        backgroundColor: 'var(--f-accent-subtle)',
        color: 'var(--f-fg)',
      };
    }

    return {
      borderColor: hoveredValue === value ? 'var(--f-accent)' : 'var(--f-border)',
      backgroundColor: 'var(--f-bg-raised)',
      color: 'var(--f-fg)',
    };
  }

  function getIcon(value: boolean) {
    if (!isGraded || correctAnswer === null) return null;
    const isSelected = selected === value;
    const isCorrectChoice = value === correctAnswer;

    if ((isSelected && isCorrectChoice) || (!isSelected && isCorrectChoice)) {
      return (
        <motion.span
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 500, damping: 25 }}
        >
          <Check size={20} />
        </motion.span>
      );
    }
    if (isSelected && !isCorrectChoice) {
      return (
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          <X size={20} />
        </motion.span>
      );
    }
    return null;
  }

  return (
    <StepShell>
      <PromptRenderer
        doc={snapshot.prompt as TiptapNode}
        media={mediaItems}
        diagramJson={diagramJson ?? null}
      />

      <div
        className="flex gap-4"
        style={{
          opacity: isDisabled ? 0.7 : 1,
          pointerEvents: isDisabled ? 'none' : 'auto',
        }}
      >
        {([true, false] as const).map((value, index) => (
          <motion.button
            key={String(value)}
            type="button"
            disabled={isDisabled || isGraded}
            onClick={() => handleSelect(value)}
            onMouseEnter={() => !isGraded && !isDisabled && setHoveredValue(value)}
            onMouseLeave={() => setHoveredValue(null)}
            whileHover={
              !isGraded && !isDisabled
                ? { scale: 1.02, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)' }
                : undefined
            }
            whileTap={!isGraded && !isDisabled ? { scale: 0.98 } : undefined}
            animate={
              isGraded && selected === value && value === correctAnswer
                ? { scale: [1, 1.02, 1] }
                : undefined
            }
            transition={{ type: 'spring', stiffness: 500, damping: 28 }}
            style={{
              ...getButtonStyle(value),
              flex: 1,
              padding: '24px 16px',
              borderRadius: 'var(--f-radius-xl)',
              borderWidth: '2px',
              borderStyle: 'solid',
              fontFamily: 'var(--f-font-sans)',
              fontSize: 'var(--f-text-xl)',
              fontWeight: 600,
              cursor: isGraded ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--f-space-2)',
              position: 'relative',
              overflow: 'visible',
              transition: 'border-color 0.2s ease',
              outline: 'none',
            }}
          >
            <RippleFlash active={rippleIndex === index} rippleKey={rippleKey} />
            <AnimatedKbd show={!isGraded && showShortcuts}>{index + 1}</AnimatedKbd>
            <span>{value ? 'True' : 'False'}</span>
            <AnimatePresence mode="wait">{getIcon(value)}</AnimatePresence>
          </motion.button>
        ))}
      </div>

      {isGraded && result && <FeedbackDisplay result={result} />}
    </StepShell>
  );
}
